import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { ACTIVE_ACCESS, TRIAL_URGENT_DAYS } from './access';
import { UPGRADE_PATH } from './plan-limits';

const formatDate = (iso: string, lang: string) => {
  try {
    return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'long' });
  } catch {
    return iso.slice(0, 10);
  }
};

/**
 * "Trial · 9 days left", in the top bar, for a vendor on a running trial
 * (docs/pricing-v2.md). One click opens what the trial is and what happens
 * when it ends; the button goes to the plans. Nothing for anyone else.
 */
export function TrialPill({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const vp = useVendorPlan();
  const isTrial = !!vp.isTrial;
  const access = vp.access ?? ACTIVE_ACCESS;
  const [open, setOpen] = useState(false);
  if (!isTrial) return null;

  const days = access.daysLeft;
  const urgent = days !== null && days <= TRIAL_URGENT_DAYS;
  const label = days === null
    ? t('PRICING.TRIAL.PILL_NO_CLOCK')
    : days <= 0
      ? t('PRICING.TRIAL.PILL_TODAY')
      : t('PRICING.TRIAL.PILL', { count: days });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="trial-pill"
          data-urgent={urgent ? 'true' : 'false'}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors hover:border-foreground/40',
            urgent ? 'border-foreground text-foreground' : 'text-muted-foreground',
            className,
          )}
        >
          <span className={cn('size-1.5 rounded-full', urgent ? 'bg-destructive' : 'bg-success')} aria-hidden />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4" data-testid="trial-popover">
        <p className="text-sm font-medium">{t('PRICING.TRIAL.POPOVER_TITLE')}</p>
        {access.trialEndsAt && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('PRICING.TRIAL.ENDS_ON', { date: formatDate(access.trialEndsAt, i18n.language) })}
          </p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t('PRICING.TRIAL.POPOVER_BODY')}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 w-full"
          onClick={() => {
            setOpen(false);
            navigate(UPGRADE_PATH);
          }}
        >
          {t('PRICING.TRIAL.CHOOSE_PLAN')}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
